import {
    AutocompleteSearchType,
    CreateLocationRequest,
    CreateRouteRequest,
    CustomShape,
    DirectionType,
    GeoJsonGeometry,
    GeoJsonType,
    GetPointResponse,
    MapDrawer,
    RouteLeg,
    RouteResponse
} from '../types';
import { LitElement, css, html } from 'lit';
import { addShapeToMap, createShapeHolderFromCustomShape, setShapeOptions } from '../helpers/shapes';
import { checkIfRouteEligible, incrementRouteCount } from '../helpers/api/account-api';
import { customElement, query, state } from 'lit/decorators';
import { delay, getFromLocalStorage, getMarkerElement, isDefined, setMarkerMap, updateMarkerVisibility } from '../helpers/utils';
import { getRoute, maneuver_icon_mapping } from '../helpers/api/routes-api';
import { revertIconForMarker, setRouteIconForMarker } from '../helpers/markers';

import { Checkbox } from '@material/mwc-checkbox';
import { Marker } from '@twitchaxorg/markerclusterer';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import { palette } from '../style/variables';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('get-route-component')
export class GetRouteComponent extends LitElement {
    static amplitude_callsite: string = 'Get Route Component';

    @query('#pinned_from')
    pinnedFromElement?: Checkbox;

    @query('#pointsList')
    pointsListElement!: HTMLUListElement;



    @state()
    startLocation?: CreateLocationRequest;

    @state()
    startMarker?: Marker;

    @state()
    showDifferentStart = false;

    @state()
    endLocation?: CreateLocationRequest;

    @state()
    showDifferentEnd = false;

    @state()
    endMarker?: Marker;

    @state()
    points: GetPointResponse[] = [];

    @state()
    showRoute = false;

    @state()
    optimizeWaypoints = false;

    @state()
    pointOrder: GetPointResponse[] = [];

    @state()
    optimizedRoute?: RouteResponse;

    @state()
    transitOption: DirectionType = DirectionType.drive;

    @state()
    routePolylines: google.maps.Polyline[] = [];

    @state()
    showDirections = false;

    @state()
    addAfterIndex?: number;

    @state()
    userIsRouteEligible = false;

    @state()
    isReady = false;

    private readonly bindAccountInfo = new StateController(this, accountInfo);

    async firstUpdated () {
        this.pointOrder = this.points;
        this.userIsRouteEligible = await checkIfRouteEligible(GetRouteComponent.amplitude_callsite);
        this.isReady = true;
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('pointOrder') || changedProperties.has('startLocation') || changedProperties.has('endLocation')) {
            await(delay(1000));
            this.updateMapIcons();
        }
    }

    updateMapIcons() {
        // for each point, update with the appropriate icon
        for (const point of this.pointOrder) {
            if (!this.startLocation && this.pointOrder.indexOf(point) === 0) {
                setRouteIconForMarker(pointCache.getMarker(point._id.$oid), '▶');
            } else if (!this.endLocation && this.pointOrder.indexOf(point) === this.pointOrder.length - 1) {
                setRouteIconForMarker(pointCache.getMarker(point._id.$oid), '◼');
            }
            else {
                let letter_index = this.pointOrder.indexOf(point);
                if (!this.startLocation) {
                    letter_index--;
                }
                const marker = pointCache.getMarker(point._id.$oid);
                setRouteIconForMarker(marker,String.fromCharCode(65 + letter_index));
            }
        }
    }

    revertMapIcons() {
        // for each point, revert icon
        for (const point of this.pointOrder) {
            const marker = pointCache.getMarker(point._id.$oid);
            revertIconForMarker(marker);
        }
    }

    get optimizeEligible() {
        let points = this.pointOrder.length;
        if (this.startLocation) {
            points++;
        }
        if (this.endLocation) {
            points++;
        }
        return points > 3;
    }

    get routeEligible() {
        let points = this.pointOrder.length;
        if (this.startLocation) {
            points++;
        }
        if (this.endLocation) {
            points++;
        }
        return points > 1 && points < 25;
    }

    get tooManyPoints() {
        let points = this.pointOrder.length;
        if (this.startLocation) {
            points++;
        }
        if (this.endLocation) {
            points++;
        }
        return points >= 25;
    }

    moveUp(point: GetPointResponse) {
        const index = this.pointOrder.findIndex(p => p === point);
        if (index > 0) {
            const [removed] = this.pointOrder.splice(index, 1);
            this.pointOrder.splice(index - 1, 0, removed);
            this.requestUpdate();
            this.updateMapIcons();
        }
    }

    moveDown(point: GetPointResponse) {
        const index = this.pointOrder.findIndex(p => p === point);
        if (index < this.pointOrder.length - 1) {
            const [removed] = this.pointOrder.splice(index, 1);
            this.pointOrder.splice(index + 1, 0, removed);
            this.requestUpdate();
            this.updateMapIcons();
        }
    }

    async onChangeDirectionType(directionType: DirectionType) {
        this.transitOption = directionType;
    }

    resetAll() {
        this.startLocation = undefined;
        this.endLocation = undefined;
        // for each point reset
        this.pointOrder.forEach(point => {
            const marker = pointCache.getMarker(point._id.$oid);
            revertIconForMarker(marker);
        });
        this.pointOrder = this.points;
        this.transitOption = DirectionType.drive;
        this.showDirections = false;
        this.routePolylines.forEach(polyline => polyline.setMap(null));
        this.routePolylines = [];

        if (this.startMarker) {
            updateMarkerVisibility(this.startMarker, false);
        }
        if (this.endMarker) {
            updateMarkerVisibility(this.endMarker, false);
        }
        this.startMarker = undefined;
        this.endMarker = undefined;
        this.showDifferentStart = false;
        this.showDifferentEnd = false;
        this.optimizedRoute = undefined;
    }

    async createGoogleMapsURL() {
        const url = new URL('https://www.google.com/maps/dir/?api=1');
        // set first location as origin param and last location as destination param
        url.searchParams.set('origin', this.pointOrder[0].location.search);

        url.searchParams.set('destination', this.pointOrder[this.pointOrder.length - 1].location.search);
        // join all the waypoints with a pipe and percent encode the result
        const waypoints = this.pointOrder.slice(1, this.pointOrder.length - 1).map(p => p.location.search);
        if (waypoints.length > 0) {
            url.searchParams.set('waypoints', waypoints.join('|'));
        }
        // set the travel mode
        let travelMode: string;
        switch (this.transitOption) {
            case DirectionType.drive:
                travelMode = 'driving';
                break;
            case DirectionType.bike:
                travelMode = 'bicycling';
                break;
            case DirectionType.walk:
                travelMode = 'walking';
                break;
            case DirectionType.transit:
                travelMode = 'transit';
                break;
            default:
                travelMode = 'driving';
                break;
        }
        url.searchParams.set('travelmode', travelMode);

        // open the url in a new tab
        // window.open(url.toString(), topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');

        const start = this.startLocation ?? this.pointOrder[0].location;
        const end = this.endLocation ?? this.pointOrder[this.pointOrder.length - 1].location;
        const waypoint_start_index = this.startLocation ? 0 : 1;
        const waypoint_end_index = this.endLocation ? this.pointOrder.length - 1 : this.pointOrder.length - 2;
        const waypoints_array = this.pointOrder.slice(waypoint_start_index, waypoint_end_index + 1).map(p => p.location);
        // if there is a start, put first location in waypoints


        const request: CreateRouteRequest = {
            origin: start,
            destination: end,
            intermediates: waypoints_array,
            travel_mode: this.transitOption,
            optimize_order: this.optimizeWaypoints
        };
        try{
            this.optimizedRoute = await getRoute(GetRouteComponent.amplitude_callsite, request);
            await incrementRouteCount(GetRouteComponent.amplitude_callsite);
        } catch (e) {
            this.optimizedRoute = {
                routes: []
            };
            getMainPage().errorToastElement.showToastAndThrow('Could not get route');
        }

        // for each polyline, add it to the map
        for (const leg of this.optimizedRoute.routes[0].legs) {
            const encodedPolyline = leg.polyline.encodedPolyline;
            const googlePolyline = new google.maps.Polyline();
            const firstEncodedPolyline = google.maps.geometry.encoding.decodePath(encodedPolyline);
            googlePolyline.setPath(firstEncodedPolyline);
            // add an id with the index of the polyline
            googlePolyline.set('id', this.optimizedRoute.routes[0].legs.indexOf(leg));
            if (topicData.map) {
                googlePolyline.setMap(topicData.map);
            }
            googlePolyline.setOptions({
                strokeColor: palette.successGreen,
                strokeWeight: 4,
            });
            this.routePolylines.push(googlePolyline);
        }

        // reorder the points based on the optimized order
        if (isDefined(this.optimizedRoute.routes[0].optimizedIntermediateWaypointIndex)) {
            const optimizedOrder = this.optimizedRoute.routes[0].optimizedIntermediateWaypointIndex;

            const optimizedPoints = [];
            // if there is not a custom start location, add the first point
            if (!this.startLocation) {
                optimizedPoints.push(this.pointOrder[0]);
            }
            for (const index of optimizedOrder) {
                const indexed = index + (this.startLocation ? 0 : 1);
                if (isDefined(this.pointOrder[indexed])) {
                    optimizedPoints.push(this.pointOrder[indexed]);
                }
            }
            if (!this.endLocation) {
                optimizedPoints.push(this.pointOrder[this.pointOrder.length - 1]);
            }
            this.pointOrder = optimizedPoints;
            this.updateMapIcons();
        }
    }

    onSelectNewStart(e: CustomEvent) {
        if (this.startMarker) {
            updateMarkerVisibility(this.startMarker, false);
        }

        const location = e.detail.value as CreateLocationRequest;
        this.startLocation = location;

        const icon_letter =  makeColorsIntoNumberSvg(
            palette.successGreen,
            '▶'
        );
        if (location.latLng) {
            const position = new google.maps.LatLng(location.latLng.lat, location.latLng.lng);
             const icon: google.maps.Icon = {
                url: icon_letter,
                scaledSize: new google.maps.Size(24, 24),

            };
            this.startMarker = getMarkerElement(topicData.map, position, icon, 30, pointCache.advancedMarkers,undefined,'Start');
            updateMarkerVisibility(this.startMarker, true);
        }
    }

    onSelectNewEnd(e: CustomEvent) {
        if (this.endMarker) {
            updateMarkerVisibility(this.endMarker, false);
        }

        const location = e.detail.value as CreateLocationRequest;
        this.endLocation = location;

        const icon_letter =  makeColorsIntoNumberSvg(
            palette.successGreen,
            '◼'
        );
        if (location.latLng) {
            const position = new google.maps.LatLng(location.latLng.lat, location.latLng.lng);
             const icon: google.maps.Icon = {
                url: icon_letter,
                scaledSize: new google.maps.Size(24, 24),

            };
            this.endMarker = getMarkerElement(topicData.map, position, icon, 30, pointCache.advancedMarkers,undefined,'End');
            updateMarkerVisibility(this.endMarker, true);
        }
    }

    async addToMap() {
        // add the route to the map
        if (this.optimizedRoute && topicData.map && topicData.mapComponent) {
            // create multipolyline
            const multipolylineGeometry = this.optimizedRoute.routes[0].legs.map((leg) => {
                return google.maps.geometry.encoding.decodePath(leg.polyline.encodedPolyline);
            });

            const geometry: Array<Array<[number, number]>> = multipolylineGeometry.map((line) => {
                return line.map((latLng) => [latLng.lng(), latLng.lat()]);
            });

            const geojson: GeoJsonGeometry = {
                type: GeoJsonType.MultiLineString,
                coordinates: geometry
            };

            const shape: CustomShape = {
                name: `Custom Route ${(topicData.topic?.custom_shapes?.length ?? 0) + 1}`,
                description: undefined,
                geojson,
                style: {
                    stroke_color: topicData.backgroundColor ?? undefined,
                    stroke_weight: 4,
                    stroke_opacity: 1,
                    fill_color: topicData.backgroundColor ?? undefined,
                    fill_opacity: 0.5,
                }
            };

            topicData.mapComponent.foucsedShapeHolder = createShapeHolderFromCustomShape(shape);
            addShapeToMap(topicData.mapComponent.foucsedShapeHolder.inner, topicData.mapComponent.googleMapComponent.map);
            setShapeOptions(topicData.mapComponent.foucsedShapeHolder.inner, {clickable: false});

            // remove existing routes
            this.routePolylines.forEach((polyline) => polyline.setMap(null));
            this.routePolylines = [];

            // remove temporary start and end markers and reset icons for points
            if (this.startMarker) {
                setMarkerMap(this.startMarker, null);
                this.startMarker = undefined;
            }
            if (this.endMarker) {
                setMarkerMap(this.endMarker, null);
                this.endMarker = undefined;
            }
            this.revertMapIcons();

            await topicData.mapComponent.setMapDrawer(MapDrawer.EditShape);
            if (isDefined(topicData.mapComponent.shapeModalContent)) {
                topicData.mapComponent.shapeModalContent.showSelectShapeType = true;
            }
        }
    }

    print () {
        // Create printable HTML.
        // Get leg details
        const locationList: HTMLElement = document.createElement('div');
        const directionList: HTMLElement = document.createElement('div');

        this.optimizedRoute?.routes.forEach((route, routeIndex) => {
                // Add directions
                route.legs.forEach((leg, legIndex) => {
                        // Add start and end locations with trip details
                        const startLocationText = legIndex === 0 && this.startLocation ? this.startLocation.search : this.pointOrder[legIndex]?.name ?? this.pointOrder[legIndex]?.location.search ?? '';
                        const startLocation = `${startLocationText ?? ''}`;
                        const endLocationText = legIndex === route.legs.length - 1 && this.endLocation ? this.endLocation.search : this.pointOrder[legIndex + 1]?.name ?? this.pointOrder[legIndex + 1]?.location?.search ?? '';
                        const endLocation = `${endLocationText ?? ''}`;
                        const distance = leg.localizedValues.distance?.text ?? 'Unknown Distance';
                        const duration = leg.localizedValues.duration?.text ?? 'Unknown Time';
                        locationList.innerHTML += `
                            <div class="location-pair">
                                <div class="start">${startLocation}</div>
                                <div class="end">${endLocation}</div>
                                <div class="trip-info">Trip Distance: ${distance} | Estimated Time: ${duration}</div>
                            </div>
                        `;
                    let legDirections = `<div class="leg"><div class="leg-title">Directions from ${startLocation} to ${endLocation}</div>`;

                    if (leg.steps) {
                        leg.steps.forEach((step, stepIndex) => {
                            legDirections += `
                                <div class="step">
                                    <strong>Step ${stepIndex + 1}:</strong> ${step.navigationInstruction?.instructions ?? ''}
                                    <br> <small>(${step.localizedValues?.distance?.text ?? ''}, ${step.localizedValues?.duration?.text ?? step.localizedValues?.staticDuration?.text ?? ''})</small>
                                </div>
                            `;
                        });
                    }

                    legDirections += `</div>`; // Close the leg div
                    directionList.innerHTML += legDirections;
                });
            }
        );

        const htmlContent = `
           <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Route Directions (${this.transitOption})</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                            color: #333;
                        }
                        .container {
                            max-width: 800px;
                            margin: auto;
                        }
                        h1 {
                            text-align: center;
                            font-size: 24px;
                        }
                        .locations {
                            background: #f8f8f8;
                            padding: 15px;
                            border-radius: 8px;
                            margin-bottom: 20px;
                        }
                        .locations h2 {
                            margin-bottom: 10px;
                            font-size: 20px;
                        }
                        .location-pair {
                            margin-bottom: 15px;
                            padding: 10px;
                            border-bottom: 2px solid #ddd;
                        }
                        .location-pair:last-child {
                            border-bottom: none;
                        }
                        .start, .end {
                            font-weight: bold;
                        }
                        .trip-info {
                            font-size: 14px;
                            color: #666;
                        }
                        .directions {
                            padding: 15px;
                            border: 1px solid #ddd;
                            border-radius: 8px;
                            background: #fff;
                        }
                        .directions h2 {
                            font-size: 20px;
                            margin-bottom: 10px;
                        }
                        .leg {
                            margin-bottom: 20px;
                            padding-bottom: 10px;
                            border-bottom: 2px dashed #ddd;
                        }
                        .leg:last-child {
                            border-bottom: none;
                        }
                        .leg-title {
                            font-size: 18px;
                            font-weight: bold;
                            color: #007BFF;
                            margin-bottom: 8px;
                        }
                        .step {
                            padding: 10px;
                            border-bottom: 1px solid #eee;
                        }
                        .step:last-child {
                            border-bottom: none;
                        }
                        .step strong {
                            color: #007BFF;
                        }
                        .print-button {
                            display: block;
                            width: 100%;
                            padding: 10px;
                            margin: 20px 0;
                            text-align: center;
                            background: #007BFF;
                            color: white;
                            border: none;
                            border-radius: 5px;
                            cursor: pointer;
                            font-size: 16px;
                        }
                        .print-button:hover {
                            background: #0056b3;
                        }
                        @media print {
                            .print-button {
                                display: none;
                            }
                        }
                    </style>
            </head>
            <body>
                <div class="container">
                    <h1>Route Directions</h1>

                    <div class="locations">
                        <h2>Route Summary</h2>
                        <div id="location-list">
                            ${locationList.innerHTML}
                        </div>
                    </div>

                    <div class="directions">
                        <h2>Step-by-Step ${this.transitOption.toLowerCase()}ing Directions</hjson2>
                        <div id="direction-list">
                            ${directionList.innerHTML}
                        </div>
                    </div>

                    <button class="print-button" onclick="window.print()">Print Directions</button>
                </div>
            </body>
        </html>

        `;

        // Create a hidden iframe.
        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        iframe.style.display = 'none';

        if (!isDefined(iframe.contentDocument) || !isDefined(iframe.contentWindow)) {
            getMainPage().errorToastElement.showToastAndThrow('Could not create iframe (due to JS limitations) for printing.');
            return;
        }

        // Write the content to the iframe.
        const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Wait for the content to load and then print
        iframe.onload = () => {
            // Print.
            iframe.contentWindow?.print();

            // Cleanup after print.
            window.onafterprint = event => {
                // Release the iframe.
                document.body.removeChild(iframe);
            };
        };
    }

    removePoint(point: GetPointResponse) {
        const pointIndex = this.pointOrder.indexOf(point);
        const point_marker = pointCache.getMarker(point._id.$oid);
        if (pointIndex > -1) {
            this.pointOrder.splice(pointIndex, 1);
            this.requestUpdate();
        }
        revertIconForMarker(point_marker);
        this.updateMapIcons();
    }

    renderNoRoute() {
        return html`
            <div class="modalContainer">
                <div class="modalTitle noRoute">
                    <h4>No Route Found</h4>
                    <p>There was no route found between the locations.</p>
                    <div class="actionButtonGroup">
                        <form-button
                            size="md"
                            variant="outline"
                            class="actionButton"
                            fullWidth
                            @click="${() => {this.optimizedRoute = undefined;}}"
                            >
                            Back
                        </form-button>
                    </div>
                </div>
            </div>
        `;
    }

    renderNoAccess() {
        const uses = accountInfo.routes_uses_this_month ?? parseInt(getFromLocalStorage('route_uses_this_month') ?? '0');
        return html`
            <div class="modalContainer">
                <div class="modalTitle noRoute">
                    <h4>Access Limited</h4>
                    You have used ${uses} of ${featuresInfo.numberOfUsesOfRoutesEnabled ?? 3} route uses this month.
                    <br>
                    <br>
                    ${accountInfo.isAuthenticated ? html`
                            ${uses >= (featuresInfo.numberOfUsesOfRoutesEnabled ?? 3) ? html`
                                <form-button
                                    size="md"
                                    class="actionButton"
                                    fullWidth
                                    @click="${() => {
                                        this.dispatchEvent(new CustomEvent('open-upgrade-dialog'));
                                    }}"
                                    >
                                    Upgrade for More
                                </form-button>
                            ` : ''}
                    ` : html`
                        <p>Please log in to Proxi to use this feature.</p>
                    `}
                </div>
            </div>
        `;
    }

    getVideoDemoLink() {
        if (topicData.isWriter && accountInfo.isAuthenticated) {
            return 'https://www.loom.com/share/6290807ebe99416eb6367449bfbe1d06?sid=6d48f642-ad29-4a35-a614-bc2c84d26580';
        }
        return 'https://www.loom.com/share/d035e6484fec435ebd2693caad88b7cc?sid=8375bd19-7aef-400a-b04b-075810be649f';
    }

    render() {
        if (!this.isReady) {
            return html`
                <loading-element loadingString="Loading Route Builder">
                </loading-element>
            `;
        }
        const points = this.pointOrder ?? [];
        if (this.optimizedRoute && this.optimizedRoute.routes.length === 0) {
            return this.renderNoRoute();
        }
        if (!this.userIsRouteEligible) {
            return this.renderNoAccess();
        }
        return html`
            <div class="modalContainer">
                <div class="scrollableContent">
                    ${this.optimizedRoute ? html`
                        <div class="modalTitle">
                            <h4>Optimized Route</h4>
                            <p>
                                ${this.optimizedRoute?.routes[0].localizedValues.duration?.text ? html`
                                    <span>${this.optimizedRoute?.routes[0].localizedValues.duration?.text}</span>
                                ` : ''}
                                ${this.optimizedRoute?.routes[0].localizedValues.distance?.text ? html`
                                    <span>${this.optimizedRoute?.routes[0].localizedValues.distance?.text}</span>
                                ` : ''}
                            </p>
                            <form-checkbox
                                class="add-modal__topic"
                                ?checked="${this.showDirections}"
                                @change="${(e: any) => {this.showDirections = (e.detail as HTMLInputElement).checked;}}">
                                    <span>Show Directions</span>
                            </form-checkbox>
                        </div>
                    ` : ''}
                    <div class="form-button-wrapper" style="margin-bottom: 10px;display: flex;justify-content: center;border-bottom: 1px solid #ddd;">
                        <form-button
                            size="xs"
                            @click="${() => {
                                window.open(this.getVideoDemoLink(), '_blank');
                            }
                            }" style="margin-bottom: 10px;">
                            <mwc-icon class="shareButtonIcon">play_circle</mwc-icon>
                            <span class="buttonText">Help Video</span>
                        </form-button>
                    </div>
                    <div id="pointsList">
                        ${this.showDifferentStart ? html`
                            ${!this.startLocation && !this.optimizedRoute ? html`
                                <proxi-input-place @value-changed="${this.onSelectNewStart}"
                                ></proxi-input-place>
                                ` : html`
                                    <div class="listSection startEnd">
                                        <span class="orderLetter">
                                            <span>▶</span>
                                        </span>
                                        <span class="pointName">Custom Start: ${this.startLocation?.search}</span>
                                        ${!this.optimizedRoute ? html`
                                            <mwc-icon class="arrow" @click="${() => {
                                                this.startLocation = undefined;
                                                if (this.startMarker) {
                                                    updateMarkerVisibility(this.startMarker, false);
                                                }
                                                this.showDifferentStart = false;
                                            }}">close</mwc-icon>
                                        ` : ''}
                                    </div>
                                    ${this.optimizedRoute ? html`${this.renderLeg(0)}` : html``}
                            `}
                        ` : html`
                            ${!this.optimizedRoute ? html`
                                <div class="form-button-wrapper">
                                    <form-button
                                        size="xs"
                                        variant="outline"
                                        icon="start"
                                        @click="${() => {
                                            this.showDifferentStart = !this.showDifferentStart;
                                        }}"
                                        >Custom Start Location</form-button>
                                </div>
                            ` : ''}
                        `}
                        ${!this.optimizedRoute && points.length === 0 ? html`
                            ${this.renderAddWaypoint(-1)}
                        ` : ''}
                        ${points.map((point: GetPointResponse, index: number) => {
                            const isStart = index === 0 && !this.startLocation;
                            const isEnd = index === points.length - 1 && !this.endLocation;
                            const char = isStart || isEnd ? (isStart ? '▶' : '◼') : this.startLocation ? String.fromCharCode(65 + index) : String.fromCharCode(65 + index -1);
                            return html`
                                ${index === 0 && this.startLocation ? html`
                                    ${this.renderAddWaypoint(index - 1)}
                                ` : ''}
                                <div class="listSection ${isStart || isEnd ? 'startEnd' : ''}">
                                    <span class="orderLetter">
                                        <span>${char}</span>
                                    </span>
                                    <span class="pointName">
                                        ${index === 0 && !this.startLocation ? 'Start: ' : index === points.length - 1 && !this.endLocation ? 'End: ' : ''}
                                        ${point.name ?? point.location.search}</span>
                                    ${this.optimizedRoute ? '' : html`
                                        <mwc-icon class="arrow" @click="${() => this.moveUp(point)}">arrow_upward</mwc-icon>
                                        <mwc-icon class="arrow" @click="${() => this.moveDown(point)}">arrow_downward</mwc-icon>
                                        <mwc-icon class="arrow close" @click="${() => this.removePoint(point)}">close</mwc-icon>
                                    `}
                                </div>
                                ${index < points.length - 1 || this.endLocation ? html`
                                    ${this.renderAddWaypoint(index)}
                                ` : ''}
                                ${this.optimizedRoute ? html`
                                    ${this.renderLeg(this.startLocation ? index + 1 : index)}
                                ` : ''}
                            `;
                        }
                        )}
                        ${!this.optimizedRoute && points.length === 1 ? html`
                            ${this.renderAddWaypoint(0)}
                        ` : ''}
                        ${this.showDifferentEnd ? html`
                            ${!this.endLocation && !this.optimizedRoute ? html`
                                <proxi-input-place @value-changed="${this.onSelectNewEnd}"
                                ></proxi-input-place>
                                ` : html`
                                    <div class="listSection startEnd">
                                        <span class="orderLetter">
                                            <span>◼</span>
                                        </span>
                                        <span class="pointName">Custom End: ${this.endLocation?.search}</span>
                                        ${!this.optimizedRoute ? html`
                                            <mwc-icon class="arrow" @click="${() => {
                                                this.endLocation = undefined;
                                                if (this.endMarker) {
                                                    updateMarkerVisibility(this.endMarker, false);
                                                }
                                                this.showDifferentEnd = false;
                                            }}">close</mwc-icon>
                                        ` : ''}
                                    </div>
                            `}
                        ` : html`
                            ${!this.optimizedRoute ? html`
                                <div class="form-button-wrapper">
                                    <form-button
                                    size="xs"
                                    variant="outline"
                                    icon="last_page"
                                    @click="${() => {
                                        this.showDifferentEnd = !this.showDifferentEnd;
                                    }}"
                                    >Custom End Location</form-button>
                                </div>
                            ` : ''}
                        `}
                    </div>
                </div>
                ${this.optimizedRoute ? '' : html`
                    <div class="transportationTypeTabs">
                        <div class="transitOption" ?selected=${this.transitOption === DirectionType.drive} @click=${async () => await this.onChangeDirectionType(DirectionType.drive)}>
                            <mwc-icon>directions_car</mwc-icon>
                            <span class="directionsText">Drive</span>
                        </div>
                        <div class="transitOption" ?selected=${this.transitOption === DirectionType.bike} @click=${async () => await this.onChangeDirectionType(DirectionType.bike)}>
                            <mwc-icon>directions_bike</mwc-icon>
                            <span class="directionsText">Bike</span>
                        </div>
                        <div class="transitOption" ?selected=${this.transitOption === DirectionType.walk} @click=${async () => await this.onChangeDirectionType(DirectionType.walk)}>
                            <mwc-icon>directions_walk</mwc-icon>
                            <span class="directionsText">Walk</span>
                        </div>
                        <div class="transitOption" ?selected=${this.transitOption === DirectionType.transit} @click=${async () => await this.onChangeDirectionType(DirectionType.transit)}>
                            <mwc-icon>directions_transit</mwc-icon>
                            <span class="directionsText">Transit</span>
                        </div>
                    </div>
                    ${this.optimizeEligible ? html`
                        <div style="width: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                            <form-checkbox
                                    class="add-modal__topic"
                                    ?checked="${this.optimizeWaypoints}"
                                    @change="${(e: any) => {this.optimizeWaypoints = (e.detail as HTMLInputElement).checked;}}">
                                        <span>Optimize Waypoint Order</span>
                            </form-checkbox>
                            ${this.optimizeWaypoints ? html`
                                <span style="font-size: 12px;">Your Start and End locations will be used as the first and last stops in the route. All other stops will be reordered to create the most efficient route.</span>
                            ` : ''}
                        </div>
                    ` : ''}
                `}
                ${!this.optimizedRoute ? html`
                    ${this.tooManyPoints ? html`
                        <div class="modalTitle">
                            <h4>Too Many Points</h4>
                            <p>There are too many points in this route (>25). Please remove some points to continue or contact Proxi for a more powerful route feature.</p>
                        </div>
                    ` : ''}
                    <div class="actionButtonGroup">

                        <form-button
                            size="md"
                            class="actionButton"
                            fullWidth
                            ?disabled="${!this.routeEligible}"
                            @click="${async () => await this.createGoogleMapsURL()}"
                            >
                            Generate Route
                        </form-button>
                    </div>
                ` : html`
                    <div class="actionButtonGroup">
                        <form-button
                            variant="outline"
                            size="md"
                            class="actionButton"
                            fullWidth
                            @click="${this.addToMap}"
                            >
                            Add Route To Map
                        </form-button>
                        <form-button
                            variant="outline"
                            size="md"
                            class="actionButton"
                            fullWidth
                            @click="${this.print}"
                            >
                            Print
                        </form-button>
                    </div>
                `}
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    addWaypoint(point_id: string) {
        const index = this.addAfterIndex;
        const waypoint = pointCache.allPoints.find(p => p._id.$oid === point_id);
        if (!waypoint || index === undefined) {
            return;
        }
        // add the waypoint to the list of points
        const real_index = index + 1;
        this.pointOrder.splice(real_index, 0, waypoint);
        this.requestUpdate();

        this.addAfterIndex = undefined;
        this.updateMapIcons();
    }


    renderAddWaypoint(index: number) {
        if (this.optimizedRoute) {
            return html``;
        }
        if (this.addAfterIndex === undefined) {
            return html`
                <div class="form-button-wrapper">
                    <form-button
                        size="xs"
                        variant="outline"
                        icon="add"
                        @click="${() => {
                            this.addAfterIndex = index;
                        }}"
                        >Add Waypoint</form-button>
                </div>
            `;
        }
        if (this.addAfterIndex !== index) {
            return html``;
        }
        if (this.addAfterIndex === index) {
            return html`
                <div class="listSection">
                        <proxi-autocomplete
                        .searchLimit="${10}"
                        .searchTypes="${[AutocompleteSearchType.Point]}"
                        style="max-height: 50px;width: 300px;z-index: 4;"
                        @on-select-point="${async (e: CustomEvent) => {
                            this.addWaypoint(e.detail);
                        }}"
                        @close-search="${() => {
                            this.addAfterIndex = undefined;
                        }}"
                    ></proxi-autocomplete>
                </div>
            `;
        }
    }

    renderLeg(leg_index: number) {
        const leg_to_next = this.optimizedRoute?.routes[0].legs[leg_index];
        if (!leg_to_next) {
            return html``;
        }
        return html`
            <div class="listSection routeDetail" style="${this.showDirections ? 'margin: 0' : ''}"
                @mouseenter="${() => {
                // hover over the polyline with the same id as the index of the point
                const polyline = this.routePolylines[leg_index];
                polyline?.setOptions({ strokeWeight: 8, strokeColor: 'black'});
                }}" @mouseleave="${() => {
                const polyline = this.routePolylines[leg_index];
                polyline?.setOptions({ strokeWeight: 4, strokeColor: palette.successGreen});
                }}">
                <span class="pointName legDetails" style="${this.showDirections ? '' : 'border-bottom: none; padding-bottom: unset;'}">
                    >${leg_to_next?.localizedValues.distance?.text} | ${leg_to_next?.localizedValues.duration?.text} ${leg_to_next?.localizedValues?.transitFare ? `| ${leg_to_next?.localizedValues?.transitFare?.text}` : ''}
                </span>
                ${this.showDirections ? this.renderSteps(leg_to_next) : ''}
            </div>
        `;
    }

    renderSteps(leg: RouteLeg) {
        const steps = leg.steps;
        return html`
            <div class="steps">
                ${steps?.map((steps) => {
                    const icon = steps.navigationInstruction?.maneuver ? maneuver_icon_mapping[steps.navigationInstruction.maneuver] : 'arrow_upward';
                    return html`
                        <div class="step">
                            <span><mwc-icon>${icon}</mwc-icon></span>
                            <span>${unsafeHTML(steps?.navigationInstruction?.instructions)}</span>
                        </div>
                        ${steps.localizedValues?.distance ? html`
                            <span class="step step_distance">
                                — Continue for ${steps.localizedValues.distance?.text} —
                            </span>
                        ` : ''}
                    `;
                }
                )}
            </div>
        `;
    }


    static get styles() {
        return css`
            :host {
                --mdc-icon-size: 16px;
            }

            .modalContainer {
                display: flex;
                flex-direction: column;
                max-width: 100%;
                height: 100%;
                overflow-y: auto;
            }

            .listSection{
                display: flex;
                flex-direction: row;
                align-items: center;
                padding: 0.5rem 1rem;
                border-radius: 0.5rem;
                flex-wrap: nowrap;
                background-color: #F7F8F9;
            }

            .routeDetail {
                background-color: var(--theme-border-color);
                margin: 0 4rem;
                text-align: center;
                font-weight: 600;
                flex-direction: column;
                cursor: pointer;
            }

            .routeDetail:hover {
                background-color: white;
            }

            .form-button-wrapper {
                width: 100%; display: flex; align-items: center; justify-content: center;
            }

            .steps {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 0.5rem;
                margin: 1rem 0;
            }

            .legDetails {
                border-bottom: black 1px solid;
                width: 100%;
                padding-bottom: 0.5rem;
            }

            .step {
                text-align: left;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
                --mdc-icon-size: 16px;
            }

            .step_distance {
                font-style: italic;
                font-size: 0.65rem;
                text-align: center;
                font-weight: 400;
            }

            .startEnd{
                background-color: var(--main-sky-color);
            }

            .orderLetter{
                display: flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                font-size: 10px;
                border-radius: 50%;
                background-color: var(--main-success-color);
                margin-right: 1rem;
                color: white;
                font-weight: bolder;
            }

            .pointName{
                flex: 1;
            }

            .transportationTypeTabs {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                margin-top: 1rem;
                margin-bottom: 1rem;
            }

            .transitOption {
                padding: 4px 8px;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
                border-radius: 0.5rem;
                cursor: pointer;
            }

            .transitOption[selected] {
                background-color: #F7F8F9;
                border: 1px solid var(--theme-border-color);
            }


            #pointsList{
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .arrow {
                cursor: pointer;
            }

            .modalTitle {
                text-align: center;
                margin: 1.5rem 0;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .actionButtonGroup {
                margin-top: 1rem;
                margin-bottom: 1rem;
                display: flex;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            .actionButtonGroup form-button {
                flex: 1;
            }

            .scrollableContent {
                flex: 1;
                overflow: auto;
            }

            .scrollableContent::-webkit-scrollbar {
                width: 5px;
            }

            .scrollableContent::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            .scrollableContent::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            @media screen and (max-width: 600px) {
                .modalContainer {
                    padding: 0 1rem;
                    min-width: 200px;
                }

                .modalTitle {
                    padding: 0 1rem;
                    font-size: 20px;
                }

                .directionsText {
                    display: none;
                }

                .transportationTypeTabs {
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                }
            }
        `;
    }
}

